import { useState, useEffect } from "react";
import { useI18n } from "../contexts/I18nContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  DownloadSimple, UploadSimple, Database, FileArrowDown, FileArrowUp,
  CheckCircle, Warning, Info, Spinner, MapPin, Buildings
} from "@phosphor-icons/react";

export default function DataManagementPage() {
  const { t } = useI18n();
  const [exportStats, setExportStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Address migration state
  const [addrRunning, setAddrRunning] = useState(false);
  const [addrReport, setAddrReport] = useState(null);
  const [addrIsDryRun, setAddrIsDryRun] = useState(true);

  // Property portfolio builder state
  const [pfRunning, setPfRunning] = useState(false);
  const [pfReport, setPfReport] = useState(null);
  const [pfIsDryRun, setPfIsDryRun] = useState(true);

  const runPortfolioBuild = async (isDryRun) => {
    if (!isDryRun && !window.confirm("Add every address used on existing requests, jobs, emergencies, contracts and projects to the owning client's portfolio and link those records? Nothing is deleted; re-running is safe.")) return;
    setPfRunning(true);
    setPfIsDryRun(isDryRun);
    try {
      const { data } = await API.post(`/admin/build-property-portfolios?dry_run=${isDryRun ? "true" : "false"}`);
      setPfReport(data);
      toast.success(isDryRun ? `Preview: ${data.properties_created} properties would be added, ${data.records_linked} records linked` : `Done: ${data.properties_created} properties added, ${data.records_linked} records linked`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Portfolio build failed");
    } finally {
      setPfRunning(false);
    }
  };

  useEffect(() => {
    API.get("/tenant/export-stats")
      .then(({ data }) => setExportStats(data))
      .catch(() => {})
      .finally(() => setLoadingStats(false));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await API.post("/tenant/export");
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tenant-export-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Export complete: ${data.stats?.total_records || 0} records exported`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Maximum 50MB.");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const { data } = await API.post("/tenant/import", json);
      setImportResult(data);
      toast.success(data.message);
      // Refresh stats
      API.get("/tenant/export-stats")
        .then(({ data }) => setExportStats(data))
        .catch(() => {});
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error("Invalid JSON file");
      } else {
        toast.error(err.response?.data?.detail || "Import failed");
      }
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const runAddressMigration = async (isDryRun) => {
    setAddrRunning(true);
    setAddrIsDryRun(isDryRun);
    try {
      const { data } = await API.post(`/admin/migrate-addresses?dry_run=${isDryRun ? "true" : "false"}`);
      setAddrReport(data);
      toast.success(isDryRun ? `Preview complete: ${data.total_updated} records would be updated` : `Migration complete: ${data.total_updated} records updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration failed");
    } finally {
      setAddrRunning(false);
    }
  };

  const COLLECTION_LABELS = {
    contracts: "Contracts",
    projects: "Projects",
    tasks: "Tasks",
    jobs: "Jobs",
    vendors: "Suppliers",
    clients: "Clients",
    invoices: "Invoices",
    work_orders: "Work Orders",
    emergency_jobs: "Emergency Jobs",
    job_requests: "Job Requests",
    document_templates: "Document Templates",
    job_form_templates: "Job Form Templates",
    users: "Users",
  };

  return (
    <div data-testid="data-management-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight" data-testid="data-management-title">
          {t("settings.data_management")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Export or import your tenant data for backup and portability</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Card */}
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="export-card">
          <div className="px-5 py-4 border-b border-border bg-emerald-500/5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <FileArrowDown size={22} className="text-emerald-600" weight="duotone" />
            </div>
            <div>
              <h2 className="text-base font-semibold font-[Chivo]">{t("settings.export_data")}</h2>
              <p className="text-xs text-muted-foreground">Download all your data as a JSON file</p>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Export Stats */}
            {loadingStats ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : exportStats ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">Data Summary</span>
                  <Badge variant="outline" className="text-xs" data-testid="export-total-records">
                    {exportStats.total_records} total records
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2" data-testid="export-stats-grid">
                  {Object.entries(exportStats.collections || {}).map(([key, count]) => (
                    <div key={key} className="flex items-center justify-between py-1.5 px-3 rounded bg-muted/30 text-xs" data-testid={`export-stat-${key}`}>
                      <span className="text-muted-foreground">{COLLECTION_LABELS[key] || key}</span>
                      <span className="font-mono font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <Info size={20} className="mx-auto text-muted-foreground mb-1" />
                <p className="text-xs text-muted-foreground">Unable to load export stats</p>
              </div>
            )}

            <div className="pt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
              <Info size={12} className="shrink-0" />
              <span>Export includes all data except password hashes. User accounts are included for reference only.</span>
            </div>

            <Button
              onClick={handleExport}
              disabled={exporting}
              className="w-full"
              data-testid="export-btn"
            >
              {exporting ? (
                <>
                  <Spinner size={16} className="mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <DownloadSimple size={16} className="mr-2" />
                  {t("common.export")} JSON
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Import Card */}
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="import-card">
          <div className="px-5 py-4 border-b border-border bg-blue-500/5 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <FileArrowUp size={22} className="text-blue-600" weight="duotone" />
            </div>
            <div>
              <h2 className="text-base font-semibold font-[Chivo]">{t("settings.import_data")}</h2>
              <p className="text-xs text-muted-foreground">Restore data from a previously exported JSON file</p>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/40 transition-colors" data-testid="import-dropzone">
              <UploadSimple size={28} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium mb-1">Upload JSON file</p>
              <p className="text-[10px] text-muted-foreground mb-3">Accepts .json files up to 50MB</p>
              <label>
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleImport}
                  disabled={importing}
                  className="hidden"
                  data-testid="import-file-input"
                />
                <Button variant="outline" size="sm" asChild disabled={importing}>
                  <span className="cursor-pointer">
                    {importing ? (
                      <>
                        <Spinner size={14} className="mr-1.5 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <UploadSimple size={14} className="mr-1.5" />
                        Choose File
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </div>

            <div className="space-y-2 text-[10px] text-muted-foreground">
              <div className="flex items-start gap-2">
                <Warning size={12} className="text-amber-500 shrink-0 mt-0.5" />
                <span>Duplicate records (by ID) are automatically skipped. User accounts are not imported for security.</span>
              </div>
              <div className="flex items-start gap-2">
                <Info size={12} className="shrink-0 mt-0.5" />
                <span>Data is imported into your current tenant. All records get your tenant ID assigned.</span>
              </div>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className="border border-border rounded-md p-4 bg-muted/20 animate-fade-in" data-testid="import-result">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle size={16} className="text-emerald-500" weight="fill" />
                  <span className="text-sm font-semibold">Import Complete</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="text-center py-2 rounded bg-emerald-500/10" data-testid="import-imported-count">
                    <p className="text-lg font-bold font-mono text-emerald-600">{importResult.total_imported}</p>
                    <p className="text-[10px] text-muted-foreground">Imported</p>
                  </div>
                  <div className="text-center py-2 rounded bg-amber-500/10" data-testid="import-skipped-count">
                    <p className="text-lg font-bold font-mono text-amber-600">{importResult.total_skipped}</p>
                    <p className="text-[10px] text-muted-foreground">Skipped</p>
                  </div>
                </div>
                {importResult.details && (
                  <div className="space-y-1" data-testid="import-details">
                    {Object.entries(importResult.details).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between text-xs py-0.5">
                        <span className="text-muted-foreground">{COLLECTION_LABELS[key] || key}</span>
                        <span className="font-mono text-[10px]">
                          <span className="text-emerald-600">{val.imported} in</span>
                          {val.skipped > 0 && <span className="text-amber-600 ml-2">{val.skipped} skip</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Address Migration Card */}
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="address-migration-card">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <MapPin size={16} className="text-primary" weight="duotone" />
              <h2 className="text-sm font-semibold font-[Chivo]">Address Migration</h2>
            </div>
            <Badge variant="outline" className="text-[10px]">Admin only</Badge>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Parse flat <code className="bg-muted px-1 rounded">property_address</code> strings on existing <strong>Invoices</strong>, <strong>Works Orders</strong>, <strong>Projects</strong> and <strong>Job Requests</strong> into structured <code className="bg-muted px-1 rounded">street_address</code> / <code className="bg-muted px-1 rounded">town_city</code> / <code className="bg-muted px-1 rounded">county</code> / <code className="bg-muted px-1 rounded">postcode</code> fields. This makes the new 2-line address format apply retroactively to every document. Idempotent — records already parsed are skipped.
            </p>

            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" disabled={addrRunning} onClick={() => runAddressMigration(true)} data-testid="address-migrate-dryrun-btn">
                {addrRunning && addrIsDryRun ? <Spinner size={14} className="animate-spin mr-1" /> : <FileArrowDown size={14} className="mr-1" />}
                Preview (Dry Run)
              </Button>
              <Button size="sm" disabled={addrRunning} onClick={() => runAddressMigration(false)} data-testid="address-migrate-run-btn">
                {addrRunning && !addrIsDryRun ? <Spinner size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
                Run Migration
              </Button>
            </div>

            {addrReport && (
              <div className="border border-border rounded-md p-3 bg-muted/20 mt-2" data-testid="address-migrate-report">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold">{addrReport.dry_run ? "Preview Results" : "Migration Results"}</span>
                  <Badge className="text-[10px] bg-emerald-500/10 text-emerald-700 border-emerald-200">
                    {addrReport.total_updated} {addrReport.dry_run ? "would update" : "updated"}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {Object.entries(addrReport.collections || {}).map(([coll, stats]) => (
                    <div key={coll} className="text-xs" data-testid={`address-report-${coll}`}>
                      <div className="flex items-center justify-between px-2 py-1 rounded bg-muted/40">
                        <span className="font-medium">{COLLECTION_LABELS[coll] || coll}</span>
                        <span className="font-mono text-[10px]">
                          <span className="text-emerald-600">{stats.updated} {addrReport.dry_run ? "would" : "done"}</span>
                          {stats.skipped > 0 && <span className="text-muted-foreground ml-2">{stats.skipped} skip</span>}
                          <span className="text-muted-foreground ml-2">({stats.scanned} scanned)</span>
                        </span>
                      </div>
                      {stats.samples?.length > 0 && (
                        <div className="mt-1 pl-2 border-l-2 border-border space-y-1">
                          {stats.samples.map((s, i) => (
                            <div key={i} className="text-[10px] text-muted-foreground">
                              <div className="font-mono truncate">&ldquo;{s.flat}&rdquo;</div>
                              <div className="font-mono pl-2">→ {s.parsed.street_address || "—"} / {s.parsed.town_city || "—"} / {s.parsed.county || "—"} / <span className="text-primary">{s.parsed.postcode || "—"}</span></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Property Portfolio Builder Card */}
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="portfolio-build-card">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Buildings size={16} className="text-primary" weight="duotone" />
              <h2 className="text-sm font-semibold font-[Chivo]">Client Property Portfolios</h2>
            </div>
            <Badge variant="outline" className="text-[10px]">Admin only</Badge>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Drop every address already used on <strong>Job Requests</strong>, <strong>Jobs</strong>, <strong>Emergency Jobs</strong>, <strong>Contracts</strong> and <strong>Projects</strong> into the owning client's property portfolio, and link each record to its property so Property History is complete. De-duplicates on first address line + postcode; records without a postcode are still imported on the address line. Site contact and access notes are carried over from the most recent record. Going forward every new record does this automatically — this is the one-off catch-up. Idempotent.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" disabled={pfRunning} onClick={() => runPortfolioBuild(true)} data-testid="portfolio-build-dryrun-btn">
                {pfRunning && pfIsDryRun ? <Spinner size={14} className="animate-spin mr-1" /> : <FileArrowDown size={14} className="mr-1" />}
                Preview (Dry Run)
              </Button>
              <Button size="sm" disabled={pfRunning} onClick={() => runPortfolioBuild(false)} data-testid="portfolio-build-run-btn">
                {pfRunning && !pfIsDryRun ? <Spinner size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
                Build Portfolios
              </Button>
            </div>
            {pfReport && (
              <div className="border border-border rounded-md p-3 bg-muted/20 mt-2" data-testid="portfolio-build-report">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <span className="text-xs font-semibold">{pfReport.dry_run ? "Preview Results" : "Build Results"}</span>
                  <div className="flex gap-1.5 flex-wrap">
                    <Badge className="text-[10px] bg-emerald-500/10 text-emerald-700 border-emerald-200" data-testid="portfolio-report-created">{pfReport.properties_created} {pfReport.dry_run ? "would be added" : "added"}</Badge>
                    <Badge className="text-[10px] bg-sky-500/10 text-sky-700 border-sky-200" data-testid="portfolio-report-matched">{pfReport.properties_matched} matched existing</Badge>
                    <Badge className="text-[10px] bg-primary/10 text-primary border-primary/20" data-testid="portfolio-report-linked">{pfReport.records_linked} records {pfReport.dry_run ? "would link" : "linked"}</Badge>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mb-2">{pfReport.clients_touched} of {pfReport.clients_scanned} clients affected · {pfReport.records_already_linked} records already linked · {pfReport.records_skipped} skipped (no address)</p>
                <div className="space-y-1">
                  {Object.entries(pfReport.collections || {}).map(([coll, s]) => (
                    <div key={coll} className="flex items-center justify-between px-2 py-1 rounded bg-muted/40 text-xs" data-testid={`portfolio-report-${coll}`}>
                      <span className="font-medium">{COLLECTION_LABELS[coll] || coll}</span>
                      <span className="font-mono text-[10px]">
                        <span className="text-emerald-600">{s.linked} {pfReport.dry_run ? "would link" : "linked"}</span>
                        {s.already_linked > 0 && <span className="text-muted-foreground ml-2">{s.already_linked} already</span>}
                        {s.skipped > 0 && <span className="text-amber-600 ml-2">{s.skipped} skip</span>}
                        <span className="text-muted-foreground ml-2">({s.scanned} scanned)</span>
                      </span>
                    </div>
                  ))}
                </div>
                {pfReport.created_samples?.length > 0 && (
                  <div className="mt-2 pl-2 border-l-2 border-border space-y-0.5" data-testid="portfolio-report-samples">
                    <p className="text-[10px] font-semibold text-muted-foreground">{pfReport.dry_run ? "Would add" : "Added"} (first {pfReport.created_samples.length})</p>
                    {pfReport.created_samples.map((s, i) => (
                      <div key={i} className="text-[10px] text-muted-foreground font-mono truncate"><span className="text-foreground">{s.client}</span> → {s.name}{s.address && s.address !== s.name ? ` · ${s.address}` : ""} <span className="opacity-60">({COLLECTION_LABELS[s.source] || s.source})</span></div>
                    ))}
                  </div>
                )}
                {pfReport.skipped_samples?.length > 0 && (
                  <div className="mt-2 pl-2 border-l-2 border-amber-300 space-y-0.5" data-testid="portfolio-report-skipped">
                    <p className="text-[10px] font-semibold text-amber-700">Skipped — fix the address on the record and re-run</p>
                    {pfReport.skipped_samples.map((s, i) => (
                      <div key={i} className="text-[10px] text-muted-foreground font-mono truncate">{COLLECTION_LABELS[s.collection] || s.collection} {s.reference} · {s.client} — {s.reason}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
